<template>
  <div>
    <base-header class="pb-8 pt-4">
      <!-- Card stats -->
      <div class="row">
        <div class="col-md-3 col-sm-12">
          <stats-card
            :title="$t('Toplam Gelir')"
            :sub-title="`${income} TL`"
            icon="fas fa-chart-bar"
            class="mb-4 mb-xl-0 bg-gradient-success">
          </stats-card>
        </div>

        <div class="col-md-3 col-sm-12">
          <stats-card
            :title="$t('Toplam Gider')"
            :sub-title="`${expense} TL`"
            icon="fas fa-chart-bar"
            class="mb-4 mb-xl-0 bg-gradient-danger">
          </stats-card>
        </div>

        <div class="col-md-3 col-sm-12">
          <stats-card
            :title="$t('Gelecek Ödemeler')"
            :sub-title="`${credit} TL`"
            icon="ni ni-diamond"
            class="mb-4 mb-xl-0 bg-gradient-info">
          </stats-card>
        </div>

        <div class="col-md-3 col-sm-12">
          <stats-card
            :title="$t('Toplam Borç')"
            type="gradient-red"
            :sub-title="`${debt} TL`"
            icon="ni ni-diamond"
            class="mb-4 mb-xl-0 bg-gradient-warning">
          </stats-card>
        </div>
      </div>
    </base-header>


    <div class="container-fluid mt--7">
      <b-card no-body>
        <b-card-header>
          <div class="align-items-center row">
            <div class="col d-flex align-items-center">
              <h3 class="card-title mb-0">{{ $t("Cari Kayıtları") }}</h3>
              <div class="text-sm ml-4" style="width:200px;">
                <b-form-group class="mb-0">
                  <b-form-select v-model="filterType" :options="[
                    { text: 'Bu hafta', value: 'weekly' },
                    { text: 'Bu ay', value: 'monthly' },
                    { text: 'Tarih seç', value: 'other' },
                  ]"/>
                </b-form-group>
              </div>
              <template v-if="filterType === 'other'">
                <div class="text-sm ml-4" style="width:200px;">
                  <b-form-group class="mb-0">
                    <b-form-input type="date" v-model="startDate"/>
                  </b-form-group>
                </div>
                <div class="text-sm ml-4" style="width:200px;">
                  <b-form-group class="mb-0">
                    <b-form-input type="date" v-model="endDate"/>
                  </b-form-group>
                </div>
              </template>
              <div class="text-sm ml-4" style="width:200px;">
                <b-button variant="primary" @click="$refs.table.refresh()">{{ $t("Güncelle") }}</b-button>
              </div>
            </div>
          </div>
        </b-card-header>

        <b-table
          ref="table"
          class="align-items-center"
          thead-class="thead-light"
          :fields="[
            { key: 'title', label: 'Başlık' },
            { key: 'category.name', label: 'Kategori' },
            { key: 'client.full_name', label: 'Müşteri Adı' },
            { key: 'client.gsm', label: 'Müşteri Telefonu' },
            { key: 'price', label: 'Ücret' },
            { key: 'date', label: 'Ödeme Tarihi' },
            { key: 'payment_type', label: 'Ödeme Şekli' },
            { key: 'actions', label: '', tdClass: 'text-right' },
          ]"
          :items="items"
          show-empty>

          <template v-slot:empty>
            <div class="text-center">
              {{ $t("Henüz hiçbir kayıt girmemişsiniz") }}!
            </div>
          </template>

          <template v-slot:table-busy>
            <div style="width: 100%; text-align:center;">
              <b-spinner label="Spinning"></b-spinner>
            </div>
          </template>

          <template v-slot:cell(title)="data">
            {{ data.item.title }}
            <b-badge variant="success" v-if="data.item.type === 'income'">{{ $t("GELİR") }}</b-badge>
            <b-badge variant="danger" v-else-if="data.item.type === 'expense'">{{ $t("GİDER") }}</b-badge>
            <b-badge variant="info" v-else-if="data.item.type === 'credit'">{{ $t("ALACAK") }}</b-badge>
            <b-badge variant="warning" v-else>{{ $t('BORÇ') }}</b-badge>
          </template>

          <template v-slot:cell(payment_type)="data">
            {{ paymentType(data.item.payment_type) }}
          </template>

          <template v-slot:cell(actions)="data">
            <b-button variant="warning" size="sm" @click="showAccounting(data.item)">{{ $t("Görüntüle") }}</b-button>
            <b-button v-if="data.item.type === 'credit'" variant="info" size="sm" @click="openTypeModal(data.item)">
              {{ $t("Ödeme Alındı") }}</b-button>
            <b-button v-if="data.item.type === 'debt'" variant="info" size="sm" @click="openToExpenseModal(data.item)">
              {{ $t("Ödeme Tamamla") }}</b-button>
            <b-button variant="danger" size="sm" @click="deleteAccountingAction(data.item.id)">Sil</b-button>
          </template>

        </b-table>

        <div class="w-100 d-flex align-items-center justify-content-center mt-2 mb-3" v-if="total > 14">
          <b-pagination v-model="currentPage" :total-rows="total" :per-page="15" />
        </div>
      </b-card>
    </div>

    <!-- Credit to Income -->
    <b-modal ref="dateModal" @hide="resetDateModal">
      <b-form-group :label="$t('Ödeme Tarihi')">
        <b-form-input v-model="dateModal.date" type="date" />
      </b-form-group>
      <template v-if="dateModal.type !== 'one_time'">
        <b-form-group :label="$t('Sonraki Fatura Tarihi')">
          <b-form-input v-model="dateModal.new_first_payment_date" type="date" />
        </b-form-group>
        <b-form-group :label="$t('Sonraki Vade')">
          <b-form-input v-model="dateModal.new_last_payment_date" type="date" />
        </b-form-group>
      </template>
      <template v-slot:modal-footer>
        <b-button @click="$refs.dateModal.hide()">{{ $t("Vazgeç") }}</b-button>
        <b-button @click="changeToIncome" variant="primary">{{ $t("Gelire Dönüştür") }}</b-button>
      </template>
    </b-modal>
    <!--#Credit to Income -->

    <!-- Debt to Expense -->
    <change-accounting-type
      :show="showChangeModal"
      :id="dateModal.id"
      to="expense"
      @hide="resetChangeModal()"
      @refresh-table="refreshTable()" />
    <!--#Debt to Expense -->

    <b-modal ref="accountingModal" :title="$t('İşlem Detayları')">
      <b-table :fields="[
        { key: 'service.name', label: 'Başlık', },
        { key: 'piece', label: 'Adet', },
        { key: 'price', label: 'Fiyat', },
        { key: 'discount', label: 'İndirim', },
        { key: 'tax', label: 'KDV Oranı', },
      ]" :items="accounting.services" bordered />
      <template v-slot:modal-footer>
        <b-button @click="$refs.accountingModal.hide()" variant="primary">{{ $t("Kapat") }}</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import ChangeAccountingType from "../../components/modal/ChangeAccountingType";
  import { mapActions } from 'vuex';
  import moment from "moment";

  export default {
    components: { ChangeAccountingType },
    mounted(){
    },
    data() {
      return {
        accounting: {},
        filterType: 'weekly',
        startDate: '',
        endDate: '',
        total: 1,
        income: '',
        expense: '',
        credit: '',
        debt: '',
        currentPage: 1,
        perPage: 15,
        dateModal: {},
        showChangeModal: false,
      }
    },
    methods: {
      ...mapActions(['getAccountings', 'changeCreditToIncome', 'deleteAccounting']),
      deleteAccountingAction(id) {
        this.$swal({
          icon: 'warning',
          title: 'Dikkat',
          text: 'İşlem kalıcı olarak silinecektir, emin misiniz?',
        }).then(ok => {
          if (ok.value) {
            this.deleteAccounting(id).then(res => {
              this.success(res.data.message);
              this.refreshTable();
            }).catch(error => {
              this.error(error.response.data.message);
            });
          }
        });
      },
      showAccounting(data) {
        this.accounting = data;
        this.$refs.accountingModal.show();
      },
      resetChangeModal() {
        this.dateModal = {id: ''};
        this.showChangeModal = false;
      },
      openToExpenseModal(id) {
        this.dateModal = {id: id};
        this.showChangeModal = true;
      },
      changeToIncome() {
        this.changeCreditToIncome({
          id: this.dateModal.id,
          date: this.dateModal.date,
          new_first_payment_date: this.dateModal.new_first_payment_date,
          new_last_payment_date: this.dateModal.new_last_payment_date,
        }).then(res => {
          this.success(res.data.message);
          this.$refs.dateModal.hide();
          this.refreshTable();
        }).catch(error => {
          this.error(error.response.data.message);
        });
      },
      resetDateModal() {
        this.dateModal = {
          id: '',
          type: 'one_time',
          date: moment().format('YYYY-MM-DD'),
          new_first_payment_date: null,
          new_last_payment_date: null,
        };
      },
      openTypeModal(data) {
        this.dateModal.id = data.id;
        this.dateModal.type = data.type_of_payment;
        if( data.type_of_payment === 'monthly' ) {
          this.dateModal.new_first_payment_date = moment(data.first_payment_date).add(1, 'M').format('YYYY-MM-DD');
          this.dateModal.new_last_payment_date = moment(data.last_payment_date).add(1, 'M').format('YYYY-MM-DD');
        } else if ( data.type_of_payment === 'yearly' ) {
          this.dateModal.new_first_payment_date = moment(data.first_payment_date).add(1, 'Y').format('YYYY-MM-DD');
          this.dateModal.new_last_payment_date = moment(data.last_payment_date).add(1, 'Y').format('YYYY-MM-DD');
        }
        this.$refs.dateModal.show();
      },
      refreshTable() {
        this.$refs.table.refresh();
      },
      paymentType(type) {
        switch (type) {
          case 'credit_card':
            return 'Kredi Kartı';
          case 'eft':
            return 'Havale / EFT';
          case 'check':
            return 'Çek';
          case 'bill':
            return 'Senet';
          default:
            return 'Nakit';
        }
      },
      items() {
        const parameters = this.parameters();
        return this.getAccountings(parameters).then(data => data.data).then(response => {

          this.income = response.income;
          this.expense = response.expense;
          this.credit = response.credit;
          this.debt = response.debt;

          const records = response.records;
          this.total = records.total;
          return records.data;
        }).catch(error => {
          this.error(error.response.data.message);
        })
      },
      parameters() {
        const params = {};
        params.type = this.filterType;
        params.supplier=true;

        if( params.type === 'other' ) {
          params.start_date = this.startDate;
          params.end_date = this.endDate;
        }

        params.page = this.currentPage;

        return params;
      }
    }
  }
</script>
